import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import iconFileDelete from "../../../assets/media/icon-file-delete.svg";
import iconSearch from "../../../assets/media/icon-search.svg";
import iconCaretLeft from "../../../assets/media/icon-caret-left.svg";
import iconCaretRight from "../../../assets/media/icon-caret-right.svg";
import iconCaretDown from "../../../assets/media/icon-caret-down.svg";
import Avatar01 from "../../../assets/media/avatar-01.png";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import Navbar from "../../navbar";
import Header from "../../header";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { useDispatch, useSelector } from "react-redux";
import { addEmployee, fetchEmployee } from "../../../store/reducers/employeeSlice.js";
import Pagination from "../dashboard/appPagination";
import userImagePlaceholder from "../../../assets/media/avatar2.jpg";
import { deleteUser, getallUser, updateUser, sendMail, resendEmail, reactivateUser } from "../../../store/reducers/userSlice.js";
import PhoneInput from "react-phone-input-2";
import ConfirmationModal from "../../common/confirmationModal.jsx";
import EditUserModal from "./EditUserModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const columnHelper = createColumnHelper();

const AppUsers = () => {
  const dispatch = useDispatch();
  const [searchBy, setSearchBy] = useState("userId");
  const [currentPage, setCurrentPage] = useState(1);
  const user = useSelector((state) => state.auth.user);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);
  const pagination = useSelector((state) => state.userdata.pagination);
  const employee = useSelector((state) => state.employee);
  const createLoading = useSelector((state) => state.employee.createLoading);
  const fetchLoading = useSelector((state) => state.employee.fetchLoading);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  const authToken = user.access_token;

  const initialValues = {
    full_name: "",
    national_id_number: "",
    email: "",
    phone: "",
    roles: [],
  };

  const validationSchema = Yup.object({
    full_name: Yup.string().required("Full name is required"),
    national_id_number: Yup.string()
      .required("National Insurance Number is required")
      .matches(/^\d+$/, "National Insurance Number must contain only numbers"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone: Yup.string().required("Phone number is required"),
    roles: Yup.array()
      .of(Yup.string())
      .min(1, "Select at least one role")
      .required("Select at least one role"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const { email, full_name, national_id_number, phone, roles } = values;
      // Extract the selected role from the roles array (since we're only allowing single selection now)
      const selectedRole = roles && roles.length > 0 ? roles[0] : "admin";

      dispatch(
        addEmployee({
          userData: {
            full_name,
            national_id_number,
            email,
            phone,
            is_admin: 1,
            user_role: selectedRole,
            company_id: user.user?.company_id || user.user?.company?.id,
          },
          accessToken: authToken,
        })
      );
      dispatch(fetchEmployee({ accessToken: authToken }));
      dispatch(getallUser({ authToken: user.access_token }));
      formik.resetForm();
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const {
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    errors,
    setFieldValue,
  } = formik;

  const handleNationalIdChange = (e) => {
    const value = e.target.value;
    // Allow only numbers
    if (/^\d*$/.test(value)) {
      setFieldValue("national_id_number", value);
    }
  };


  const getTotalPages = () => {
    return pagination?.totalPages || 1;
  };


  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
  };


  useEffect(() => {

    let queryParams = {
      page: currentPage,
      limit: 10,
    };

    if (searchQuery && searchQuery.trim() !== "") {
      queryParams[searchBy] = searchQuery.trim();
    }

    if (startDate) {
      queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
    }
    if (endDate) {
      queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
    }

    const delayDebounceFn = setTimeout(() => {
      dispatch(
        getallUser({ authToken: user.access_token, queryParams: queryParams })
      );
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [
    authToken,
    dispatch,
    startDate,
    endDate,
    currentPage,
    searchQuery,
    searchBy,
    user?.user_role, // Add dependency
  ]);

  const roleOptions = [
    {
      value: "admin",
      label: "Admin",
    },
    {
      value: "reviewer",
      label: "Reviewer",
    },
    {
      value: "viewer",
      label: "Viewer",
    },
  ];

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Removed useEffect for client-side filtering and sorting since server handles it

  const deleteuserdata = (id) => {
    const config = {
      accessToken: authToken,
      user_id: id,
    };
    dispatch(deleteUser({ configdata: config }));
    dispatch(getallUser({ authToken: user.access_token }));
  };

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const handleEditClick = (userData) => {
    setEditingUser(userData);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (userId, values) => {
    const payload = {
      userId,
      userData: {
        ...values,
        accessToken: authToken,
      },
    };
    await dispatch(updateUser(payload));
    setShowEditModal(false);
    setEditingUser(null);
    dispatch(getallUser({ authToken: user.access_token }));
  };

  const handleResendEmail = (userData) => {
    const payload = {
      email: userData.email,
      company_id: userData.company_id || userData.company?.id,
      accessToken: user.access_token
    }
    dispatch(reactivateUser(payload));
  }

  // React Table columns
  const columns = React.useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * 10 + info.row.index + 1,
      }),
      columnHelper.accessor("id", {
        header: "User Id",
        cell: (info) => `GB-000000${info.getValue()}`,
      }),
      columnHelper.display({
        id: "name",
        header: "Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle widAvatar">
              <img
                className="avatar"
                src={row.image || userImagePlaceholder}
                alt=""
              />
              <strong style={{ whiteSpace: "nowrap" }}>
                {row.full_name}
              </strong>
              <span style={{ whiteSpace: "nowrap" }}>
                {row.national_id_number || "--"}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor("user_role", {
        header: "User role",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor("phone", {
        header: "Phone No.",
        cell: (info) => (
          <span
            style={{ whiteSpace: "nowrap" }}
          >
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor("created_at", {
        header: "Create Date",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.getValue()?.slice(0, 10)}
          </span>
        ),
      }),
      columnHelper.accessor("active", {
        header: "Status",
        cell: (info) => (
          <span>{info.getValue() ? "Active" : "Inactive"}</span>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          const isActive = row.active;
          const isDeleted = row.is_deleted;

          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  {isDeleted ? (
                    <li>
                      <Link
                        onClick={() => {
                          setShowDeleteModal(true);
                          setDeleteUserId(row.id);
                          setIsRestore(true);
                        }}
                        className="dropdown-item"
                      >
                        Restore
                      </Link>
                    </li>
                  ) : isActive ? (
                    <li>
                      <Link
                        onClick={() => {
                          setShowDeleteModal(true);
                          setDeleteUserId(row.id);
                          setIsRestore(false);
                        }}
                        className="dropdown-item"
                      >
                        Delete
                      </Link>
                    </li>
                  ) : (
                    <>
                      <li>
                        <Link
                          onClick={() => handleEditClick(row)}
                          className="dropdown-item"
                        >
                          Edit
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => handleResendEmail(row)}
                          className="dropdown-item"
                        >
                          Resend Email
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => {
                            setShowDeleteModal(true);
                            setDeleteUserId(row.id);
                            setIsRestore(false);
                          }}
                          className="dropdown-item"
                        >
                          Delete
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          );
        },
      }),

    ],
    [currentPage]
  );

  // Paginate data client-side for the table (since we have all data in filtereduser and implementing view-only pagination logic here matching the render logic above)
  /* const paginatedData = React.useMemo(() => {
    // Logic from original render: 
    // ((currentPage - 1) * 10 + 1) <= index + 1 && index < (currentPage * 10)
    // This implies slicing the data array
    const start = (currentPage - 1) * 10;
    const end = start + 10;
    return filtereduser ? filtereduser.slice(start, end) : [];
  }, [filtereduser, currentPage]); */

  const table = useReactTable({
    data: getallUserdata || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const [isRestore, setIsRestore] = useState(false);

  return (
    <div>
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          <div className="boardHeader noMg">
            <div className="boardHeadLeft">
              <h2>Users</h2>
            </div>
          </div>
        </div>
        <div className="empAdd">
          <h3 className="noMg">Add User</h3>
          <form className="addEmployee-form" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Full Name</label>
                  <input
                    name="full_name"
                    type="text"
                    placeholder="Marvin McKinney"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.full_name}
                    className={errors.full_name ? "error-input" : ""}
                  />
                  {errors.full_name && (
                    <div className="error-message">{errors.full_name}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>National Insurance Number</label>
                  <input
                    name="national_id_number"
                    type="text"
                    placeholder="123456"
                    onChange={handleNationalIdChange}
                    onBlur={handleBlur}
                    value={values.national_id_number}
                    className={errors.national_id_number ? "error-input" : ""}
                    maxLength={8}
                  />
                  {errors.national_id_number && (
                    <div className="error-message">
                      {errors.national_id_number}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Email Address </label>
                  <input
                    name="email"
                    type="text"
                    placeholder="Marvin McKinney"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    className={errors.email ? "error-input" : ""}
                  />
                  {errors.email && (
                    <div className="error-message">{errors.email}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Phone No.</label>
                  <PhoneInput
                    country={"bs"}
                    value={values.phone}
                    onChange={(phone) => formik.setFieldValue("phone", phone)}
                    onBlur={handleBlur}
                    placeholder="(242) 555-0122"
                    countryCodeEditable={false}
                    masks={{ bs: '...-....' }}
                    inputProps={{ name: "phone" }}
                    className={errors.phone ? "error-input" : ""}
                  />
                  {errors.phone && (
                    <div className="error-message">{errors.phone}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Role</label>
                  <select
                    style={{ width: "100%", padding: "10px" }}
                    name="roles"
                    onChange={(e) => {
                      setFieldValue("roles", [e.target.value]);
                    }}
                    onBlur={handleBlur}
                    value={values.roles[0] || ""}
                    className={errors.roles ? "error-input" : ""}
                  >
                    <option value="">Select Role</option>
                    {roleOptions.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  {errors.roles && (
                    <div className="error-message">{errors.roles}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>&nbsp;</label>
                  <button type="submit" disabled={createLoading}>
                    {createLoading ? (
                      <>
                        <div
                          className="text-xs spinner-border text-light"
                          style={{ width: "1rem", height: "1rem" }}
                          role="status"
                        ></div>
                        <span className="sr-only ml-5"> Loading...</span>
                      </>
                    ) : (
                      "Add User"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div className="boxAppBoard boxWhite mt-5">
          <div
            style={{ flexWrap: "wrap", gap: "10px" }}
            className="boardHeader"
          >
            <div className="boardHeadLeft">
              <h2>GB Users</h2>
            </div>
            <div
              style={{
                gap: "10px",
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
              className="boardHeadRight"
            >
              <div className="">
                <select onChange={handleSearchByChange} value={searchBy}>
                  <option value="userId">Search by ID</option>
                  <option value="username">Search by Name</option>
                  <option value="phone">Search by Phone </option>
                  <option value="email">Search by Email </option>
                  <option value="companyName">Search by Company Name </option>
                </select>
              </div>
              <div className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={handleSearchInputChange}
                  value={searchQuery}
                />
                <button>
                  <img src={iconSearch} alt="" />
                </button>
              </div>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    setDateRange(update);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead className="boardTheadcompany">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} style={{ whiteSpace: "nowrap" }}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {fetchLoading ? (
                  <tr>
                    <td
                      colSpan={table.getHeaderGroups()[0].headers.length}
                      className="text-center"
                    >
                      <div className="d-flex justify-content-center my-4">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {table.getRowModel().rows.length > 0 ? (
                      table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="boardTrowcompany">
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              style={{ whiteSpace: "nowrap" }}
                              className={
                                cell.column.id === "name"
                                  ? "boardCol"
                                  : cell.column.id === "created_at"
                                    ? "boardCol w-adj2"
                                    : ""
                              }
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={table.getHeaderGroups()[0].headers.length}
                          className="text-center"
                        >
                          <h2 className="text-danger my-4 py-4">
                            Result not found
                          </h2>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
          />
        </div>
      </div>
      <ConfirmationModal
        show={showDeleteModal && deleteUserId}
        title={`${isRestore ? "Restore" : "Delete"} User`}
        description={`Are you sure you want to ${isRestore ? "restore" : "delete"} this user?`}
        handleClose={() => {
          setShowDeleteModal(false);
          setDeleteUserId(null);
          setIsRestore(false);
        }}
        onConfirm={() => {
          deleteuserdata(deleteUserId);
          setShowDeleteModal(false);
          setDeleteUserId(null);
          setIsRestore(false);
        }}
      />
      <EditUserModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        user={editingUser}
        onUpdate={handleUpdateUser}
        isLoading={false}
      />
    </div>
  );
};

export default AppUsers;